import { Box, numericUtil } from '@cmg/common';
import React from 'react';

import { BarChart, BarChartProps } from '../../../../../common/components/charts';
import { barChartColors } from '../../../colors';
import { SChart, SDatalabChart } from '../shared/styled';

type Props = {
  // TODO replace with proper format from api
  chartData?: any;
  title?: string;
  xScaleLabel?: string;
  hideHeader?: boolean;
  testId?: string;
};

type RawItem = {
  y: number;
  x: string;
  count: number;
};

type ChartProps = BarChartProps<RawItem[]>;

export function transformData(chartData: Props['chartData'] = []): ChartProps['data'] {
  return {
    datasets: [
      {
        label: 'Mean',
        data: chartData.map(
          (item): RawItem => ({
            y: item.values.mean,
            x: item.label,
            count: item.values.count,
          })
        ),
        backgroundColor: barChartColors.lighter,
      },
      {
        label: 'Median',
        data: chartData.map(
          (item): RawItem => ({
            y: item.values.median,
            x: item.label,
            count: item.values.count,
          })
        ),
        backgroundColor: barChartColors.light,
      },
    ],
  };
}

export function getOptions(xScaleLabel?: string): ChartProps['options'] {
  return {
    scales: {
      x: {
        title: {
          text: xScaleLabel,
          display: !!xScaleLabel,
        },
      },
      y: {
        ticks: {
          callback: value =>
            typeof value === 'number' ? numericUtil.formatPercents(value, 1) : value,
        },
      },
    },
    plugins: {
      tooltip: {
        callbacks: {
          label: tooltipItem => {
            const { dataset } = tooltipItem;
            const raw = tooltipItem.raw as RawItem;

            const countString = ` | Count: ${numericUtil.formatInteger(raw.count)}`;
            const label = dataset.label ? `${dataset.label}: ` : '';
            const yLabel = numericUtil.formatPercents(tooltipItem.parsed.y);

            return `${label}${yLabel}${countString}`;
          },
        },
      },
    },
  };
}

export default function WidgetBarMeanMedianPercentage({
  chartData = [],
  title,
  xScaleLabel,
  hideHeader,
  testId,
}: Props) {
  const transformedData = React.useMemo(() => transformData(chartData), [chartData]);
  const options = React.useMemo(() => getOptions(xScaleLabel), [xScaleLabel]);

  return (
    <SDatalabChart data-test-id={testId}>
      {!hideHeader && <Box.Header title={title} />}

      <Box.Content>
        <SChart>
          <BarChart data={transformedData} barSizingMode="CMGCenteredBarPlugin" options={options} />
        </SChart>
      </Box.Content>
    </SDatalabChart>
  );
}
